import { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { ThemeToggler } from '../common/ThemeToggler';
import { HeaderProfileMenu } from './HeaderProfileMenu';
import { HeaderNavBar } from './HeaderNavBar';
import { cn } from '../../utils/cn';
import { useCommandPaletteStore } from '../../store/commandPaletteStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useAiRunStore } from '../../store/aiRunStore';
import api from '../../services/api';
import type { NotificationsResponse } from '../../types/index';

export function Header() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const open = useCommandPaletteStore((s) => s.open);
  const toggleMobileNav = usePreferencesStore((s) => s.toggleMobileNav);
  const dashboardLayout = usePreferencesStore((s) => s.dashboardLayout);
  const activeDetectorJob = useAiRunStore((s) => s.activeDetectorJob);
  const clearActiveDetectorJob = useAiRunStore((s) => s.clearActiveDetectorJob);
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss the "done" indicator after 60 seconds
  useEffect(() => {
    if (activeDetectorJob?.status === 'done') {
      autoDismissRef.current = setTimeout(() => clearActiveDetectorJob(), 60_000);
    }
    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    };
  }, [activeDetectorJob?.status, clearActiveDetectorJob]);

  const { data: notifData } = useQuery<NotificationsResponse>({
    queryKey: ['notifications-count'],
    queryFn: () => api.get('/notifications').then((r) => r.data.data),
    refetchInterval: 30_000,
    staleTime: 20_000,
    enabled: isAuthenticated,
  });
  const unreadCount = notifData?.unreadCount ?? 0;

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <header className="flex h-14 items-center justify-between px-4 lg:px-6 gap-2 sm:gap-4">
      {/* Left: nav bar (Header layout, desktop) + mobile menu + search trigger */}
      <div className="flex min-w-0 items-center gap-1 sm:gap-2">
        {/* Primary nav — Header layout, desktop only. Sits in the same row as the
            utility icons below, not a separate row, to keep this a single header. */}
        {dashboardLayout === 'header' && (
          <div className="hidden min-w-0 lg:block">
            <HeaderNavBar />
          </div>
        )}

        {/* Hamburger — opens the mobile nav drawer (< lg only) */}
        <button
          onClick={toggleMobileNav}
          aria-label="Open navigation menu"
          className="tap-target -ml-1 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Search trigger — ghost icon on mobile, full bordered pill from sm up.
            In Header layout this is hidden at lg — a ghost icon version sits in
            the right-hand icon cluster instead (next to notifications). */}
        <button
          onClick={open}
          aria-label="Search"
          className={cn(
            'tap-target flex items-center justify-center gap-2 rounded-lg px-2.5 sm:px-3 py-1.5 text-sm text-gray-400 dark:text-zinc-500 transition-all duration-200',
            'border border-transparent bg-transparent shadow-none',
            'sm:border-gray-200 sm:dark:border-zinc-700 sm:bg-white sm:dark:bg-zinc-800/60 sm:shadow-sm',
            'hover:text-gray-600 dark:hover:text-zinc-300 sm:hover:border-gray-300 sm:dark:hover:border-zinc-600',
            dashboardLayout === 'header' && 'lg:hidden',
          )}
        >
          <svg className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="hidden sm:inline">Search anything...</span>
          <kbd className="ml-2 hidden sm:inline-flex items-center gap-0.5 rounded border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 px-1.5 py-0.5 text-xs font-medium text-gray-400 dark:text-zinc-500">
            {isMac ? '⌘' : 'Ctrl'}<span className="ml-0.5">K</span>
          </kbd>
        </button>
      </div>

      {/* Detector job indicator */}
      {activeDetectorJob && (
        <div className="flex items-center">
          {activeDetectorJob.status === 'queued' && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-medium">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="9" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
              </svg>
              <span className="hidden sm:block max-w-[160px] truncate">
                Queued: {activeDetectorJob.originalFilename}
              </span>
            </div>
          )}
          {(activeDetectorJob.status === 'pending' || activeDetectorJob.status === 'running') && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-xs font-medium">
              <span className="w-3.5 h-3.5 rounded-full border-2 border-blue-300 dark:border-blue-600 border-t-blue-600 dark:border-t-blue-300 animate-spin shrink-0" />
              <span className="hidden sm:block max-w-[160px] truncate">
                Analyzing: {activeDetectorJob.originalFilename}
              </span>
              <span className="font-mono text-blue-500 dark:text-blue-400">{activeDetectorJob.progress}%</span>
            </div>
          )}
          {activeDetectorJob.status === 'done' && (
            <Link
              to={activeDetectorJob.runId ? `/ai-tools/detector/${activeDetectorJob.runId}` : '/ai-tools/history'}
              target={activeDetectorJob.runId ? '_blank' : undefined}
              rel={activeDetectorJob.runId ? 'noopener noreferrer' : undefined}
              onClick={clearActiveDetectorJob}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 text-xs font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
            >
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="hidden sm:block">Analysis ready</span>
            </Link>
          )}
          {activeDetectorJob.status === 'error' && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs font-medium">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span className="hidden sm:block">Analysis failed</span>
              <button onClick={clearActiveDetectorJob} className="ml-1 hover:text-red-900 dark:hover:text-red-100 transition-colors" aria-label="Dismiss">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Right side */}
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Search — ghost icon, Header layout desktop only, sits right next to
            notifications (the pill version in the left zone hides at lg there) */}
        {dashboardLayout === 'header' && (
          <button
            onClick={open}
            aria-label="Search"
            title={`Search (${isMac ? '⌘' : 'Ctrl'}K)`}
            className="tap-target hidden lg:flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        )}

        {/* Notification bell */}
        <button
          onClick={() => navigate('/notifications')}
          className="relative tap-target flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
          aria-label="Notifications"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Theme toggle — Sidebar layout desktop only now; in Header layout it
            lives inside the profile menu instead (HeaderProfileMenu). */}
        {dashboardLayout !== 'header' && (
          <div className="hidden lg:block">
            <ThemeToggler />
          </div>
        )}
        <div className={cn(dashboardLayout !== 'header' && 'lg:hidden')}>
          <HeaderProfileMenu />
        </div>
      </div>
    </header>
  );
}
